/**
 * SLM Deployment Console — operator face of the per-tenant SLM lifecycle.
 * Phase 5.2.1 — WQ-324 (final WQ of the SLM Deployment Framework ladder).
 *
 * Surfaces, as THREE DISTINCT controls (never one blurred toggle):
 *   Gate 1  Platform SLM-inference activation flag (DM-18) — read/write.
 *   Gate 2  Per-tenant deployment state machine — driven by the list below.
 *   Gate 3  Emergency capability halt — the separate kill switch above (the
 *           AI Command Center halt); referenced, NOT reimplemented here.
 *
 * The server enforces every gate (require_ml_action tier matrix, the activation
 * flag, the measured-RAM capability gate). This console DRIVES the WQ-322/323
 * endpoints and reflects their responses honestly — it never reimplements a gate
 * (guidance Triggers #5/#6). 403/501/422/503 are surfaced distinctly; the
 * `downgraded` flag and the container-tier (DEF-075) / fine-tune (DEF-072) /
 * empty states render honestly, never as a generic crash.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon } from '../common/Icons';
import {
  listDeployments, deploySLM, activateDeployment, deactivateDeployment,
  retireDeployment, getServingConfig, setServingConfig, getActivationReadiness,
  SLMApiError, type SLMDeployment, type ServingConfig, type ActivationReadiness,
} from '../../services/slmDeploymentService';

const STATE_COLORS: Record<string, string> = {
  provisioning: '#3b82f6', active: '#22c55e', deactivated: '#6b7280',
  retired: '#dc2626', fine_tuning: '#a855f7',
};

const Badge: React.FC<{ label: string; color: string; title?: string }> = ({ label, color, title }) => (
  <span title={title} style={{
    fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
    background: `${color}20`, color,
  }}>{label}</span>
);

/** Map an SLMApiError to an honest, human-readable banner message. */
const errorMessage = (err: unknown, action: string): string => {
  if (err instanceof SLMApiError) {
    if (err.status === 403) return `Not permitted: ${err.message} (server-enforced tier matrix)`;
    if (err.status === 501) return `Not yet available: ${err.message}`;
    if (err.status === 503) return `Capacity refused: ${err.message}`;
    return err.message;
  }
  return err instanceof Error ? err.message : `${action} failed`;
};

const SLMDeploymentConsole: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [deployments, setDeployments] = useState<SLMDeployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Gate 1 — activation flag
  const [servingConfig, setServingConfigState] = useState<ServingConfig | null>(null);

  // Readiness (per-deployment, expandable)
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<Record<string, ActivationReadiness>>({});
  const [readinessLoading, setReadinessLoading] = useState<string | null>(null);

  // Deploy modal
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [deployForm, setDeployForm] = useState({ model_id: '', tenant_id: '' });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { state?: string } = {};
      if (stateFilter) filters.state = stateFilter;
      const data = await listDeployments(authFetch, filters);
      setDeployments(data);
    } catch (err) {
      setError(errorMessage(err, 'Load'));
    }
    setLoading(false);
  };

  const fetchConfig = async () => {
    try {
      setServingConfigState(await getServingConfig(authFetch));
    } catch {
      // Non-blocking — the flag card shows "unknown" if this fails.
    }
  };

  useEffect(() => {
    if (!authLoading) { fetchData(); fetchConfig(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, stateFilter]);

  const handleToggleFlag = async () => {
    if (!servingConfig) return;
    setActionLoading(true);
    setError(null);
    try {
      const next = await setServingConfig(authFetch, !servingConfig.inference_enabled);
      setServingConfigState(next);
    } catch (err) {
      setError(errorMessage(err, 'Toggle activation flag'));
    }
    setActionLoading(false);
  };

  const runTransition = async (
    id: string,
    fn: (f: typeof authFetch, id: string) => Promise<SLMDeployment>,
    label: string,
  ) => {
    setActionLoading(true);
    setError(null);
    try {
      await fn(authFetch, id);
      await fetchData();
    } catch (err) {
      setError(errorMessage(err, label));
    }
    setActionLoading(false);
  };

  const handleCheckReadiness = async (id: string) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    setReadinessLoading(id);
    try {
      const r = await getActivationReadiness(authFetch, id);
      setReadiness(prev => ({ ...prev, [id]: r }));
    } catch (err) {
      setError(errorMessage(err, 'Readiness check'));
    }
    setReadinessLoading(null);
  };

  const handleDeploy = async () => {
    if (!deployForm.model_id.trim()) return;
    setActionLoading(true);
    setError(null);
    try {
      await deploySLM(authFetch, {
        model_id: deployForm.model_id.trim(),
        ...(deployForm.tenant_id.trim() ? { tenant_id: deployForm.tenant_id.trim() } : {}),
      });
      setShowDeployModal(false);
      setDeployForm({ model_id: '', tenant_id: '' });
      await fetchData();
    } catch (err) {
      setError(errorMessage(err, 'Deploy'));
    }
    setActionLoading(false);
  };

  const canActivate = (s: string) => ['provisioning', 'deactivated', 'fine_tuning'].includes(s);
  const canDeactivate = (s: string) => s === 'active';
  const canRetire = (s: string) => s !== 'retired';

  const short = (v: string | null) => (v ? v.slice(0, 8) : '—');

  if (loading && deployments.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading SLM deployments...</div>;
  }

  return (
    <div>
      {/* === THREE DISTINCT GATE CONTROLS === */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 20 }}>
        {/* Gate 1 — platform activation flag */}
        <div className="glass-card" style={{ padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Gate 1 · Platform</div>
          <div style={{ fontSize: 14, fontWeight: 600, margin: '4px 0 8px' }}>SLM Inference Serving</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Badge
              label={servingConfig ? (servingConfig.inference_enabled ? 'Enabled' : 'Disabled') : 'unknown'}
              color={servingConfig?.inference_enabled ? '#22c55e' : '#6b7280'}
            />
            <button onClick={handleToggleFlag} disabled={actionLoading || !servingConfig} style={{
              fontSize: 12, padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
              border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
              opacity: (actionLoading || !servingConfig) ? 0.5 : 1,
            }}>
              {servingConfig?.inference_enabled ? 'Disable' : 'Enable'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8 }}>
            Platform-wide planned enable/disable. Default OFF. Gates provisioning &amp; serving.
          </div>
        </div>

        {/* Gate 2 — per-tenant state */}
        <div className="glass-card" style={{ padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Gate 2 · Per-tenant</div>
          <div style={{ fontSize: 14, fontWeight: 600, margin: '4px 0 8px' }}>Deployment State</div>
          <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>
            {deployments.length} deployment{deployments.length === 1 ? '' : 's'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8 }}>
            Each deployment&apos;s own lifecycle (provisioning → active ⇄ deactivated → retired). Drive it per-row below.
          </div>
        </div>

        {/* Gate 3 — emergency halt (referenced, not reimplemented) */}
        <div className="glass-card" style={{ padding: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Gate 3 · Emergency</div>
          <div style={{ fontSize: 14, fontWeight: 600, margin: '4px 0 8px' }}>Capability Halt</div>
          <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>Kill switch (page header)</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8 }}>
            Emergency stop for ALL AI — distinct from the planned activation flag. Use the Emergency Halt control above.
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}>
          <option value="">All States</option>
          <option value="provisioning">Provisioning</option>
          <option value="active">Active</option>
          <option value="deactivated">Deactivated</option>
          <option value="fine_tuning">Fine-tuning</option>
          <option value="retired">Retired</option>
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowDeployModal(true)} style={{
          fontSize: 13, padding: '6px 16px', borderRadius: 6, border: 'none', fontWeight: 500, cursor: 'pointer',
          background: 'var(--accent-primary, #3b82f6)', color: '#fff',
        }}>+ Deploy SLM</button>
        <button onClick={fetchData} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: 4,
        }}><RefreshIcon size={14} /> Refresh</button>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Deployments table */}
      {deployments.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          No SLM deployments. Click &quot;Deploy SLM&quot; to provision a registry model for a tenant.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Tenant</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Model</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>State</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Served</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deployments.map(d => (
                <React.Fragment key={d.id}>
                  <tr style={{ borderBottom: '1px solid var(--border)', background: expandedId === d.id ? 'var(--bg-secondary)' : 'transparent' }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }} title={d.tenant_id}>{short(d.tenant_id)}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }} title={d.model_id}>{short(d.model_id)}</td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <Badge label={d.state} color={STATE_COLORS[d.state] || '#888'} />
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 12 }}>
                      {d.served_model_name ? (
                        <span>
                          {d.served_model_name}
                          {d.served_size_class && <span style={{ color: 'var(--text-secondary)' }}> ({d.served_size_class})</span>}
                          {d.downgraded && (
                            <span style={{ marginLeft: 6 }}>
                              <Badge label="⬇ downgraded" color="#ea580c" title="Large (8B) requested; small (3B/4B) served due to measured RAM" />
                            </span>
                          )}
                        </span>
                      ) : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button onClick={() => handleCheckReadiness(d.id)} disabled={actionLoading}
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                          {expandedId === d.id ? 'Hide' : 'Readiness'}
                        </button>
                        {canActivate(d.state) && (
                          <button onClick={() => runTransition(d.id, activateDeployment, 'Activate')} disabled={actionLoading}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #22c55e', background: '#22c55e20', color: '#22c55e', cursor: 'pointer' }}>
                            Activate
                          </button>
                        )}
                        {canDeactivate(d.state) && (
                          <button onClick={() => runTransition(d.id, deactivateDeployment, 'Deactivate')} disabled={actionLoading}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #6b7280', background: '#6b728020', color: '#6b7280', cursor: 'pointer' }}>
                            Deactivate
                          </button>
                        )}
                        {canRetire(d.state) && (
                          <button onClick={() => runTransition(d.id, retireDeployment, 'Retire')} disabled={actionLoading}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #dc2626', background: '#dc262620', color: '#dc2626', cursor: 'pointer' }}>
                            Retire
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === d.id && (
                    <tr>
                      <td colSpan={5} style={{ padding: '12px 24px', background: 'var(--bg-secondary)' }}>
                        {readinessLoading === d.id ? (
                          <div style={{ color: 'var(--text-secondary)' }}>Checking activation readiness...</div>
                        ) : readiness[d.id] ? (
                          <ReadinessDetail r={readiness[d.id]} />
                        ) : (
                          <div style={{ color: 'var(--text-secondary)' }}>No readiness data.</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Deploy modal */}
      {showDeployModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowDeployModal(false)}>
          <div className="glass-card" style={{ padding: 24, minWidth: 400, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>Deploy SLM</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: 13 }}>
                Registry Model ID *
                <input type="text" value={deployForm.model_id}
                  onChange={e => setDeployForm(f => ({ ...f, model_id: e.target.value }))}
                  placeholder="platform_models.id (UUID)"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 13 }}>
                Tenant ID (optional — defaults to your tenant)
                <input type="text" value={deployForm.tenant_id}
                  onChange={e => setDeployForm(f => ({ ...f, tenant_id: e.target.value }))}
                  placeholder="tenant UUID"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4 }} />
              </label>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={() => setShowDeployModal(false)} style={{
                  fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                }}>Cancel</button>
                <button onClick={handleDeploy} disabled={actionLoading || !deployForm.model_id.trim()} style={{
                  fontSize: 13, padding: '6px 16px', borderRadius: 6, border: 'none', fontWeight: 500, cursor: 'pointer',
                  background: 'var(--accent-primary, #3b82f6)', color: '#fff',
                  opacity: (actionLoading || !deployForm.model_id.trim()) ? 0.5 : 1,
                }}>Deploy</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Read-only reflection of the WQ-323 activation gates (no fork). */
const ReadinessDetail: React.FC<{ r: ActivationReadiness }> = ({ r }) => {
  const row = (label: string, node: React.ReactNode) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
      <strong style={{ minWidth: 150 }}>{label}</strong>
      <span>{node}</span>
    </div>
  );

  if (!r.container_tier_supported) {
    return (
      <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ padding: 10, borderRadius: 6, background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' }}>
          <strong>Container-tier SLM serving not yet available</strong> — dedicated per-tenant Ollama provisioning
          for container-isolated tenants is a tracked follow-on (DEF-075). Activation will return HTTP 501.
        </div>
        {row('Isolation level', r.isolation_level)}
      </div>
    );
  }

  const ramColor = r.ram_verdict.allowed ? (r.ram_verdict.downgraded ? '#ea580c' : '#22c55e') : '#dc2626';
  const ramLabel = r.ram_verdict.reason === 'fits_large' ? 'Fits large (8B)'
    : r.ram_verdict.reason === 'downgraded_to_small' ? 'Downgraded to small (3B/4B)'
    : 'Insufficient RAM — refused';

  return (
    <div style={{ fontSize: 13, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
      {row('Activation flag', <Badge label={r.activation_flag_enabled ? 'Enabled' : 'Disabled'} color={r.activation_flag_enabled ? '#22c55e' : '#6b7280'} />)}
      {row('Ready to activate', <Badge label={r.ready_to_activate ? 'Ready' : 'Not ready'} color={r.ready_to_activate ? '#22c55e' : '#dc2626'} />)}
      {row('Measured RAM verdict', <Badge label={ramLabel} color={ramColor} title={`${r.available_ram_mb} MB free`} />)}
      {row('Free RAM', `${r.available_ram_mb} MB (large ≥ ${r.thresholds.large_min_free_ram_mb}, small ≥ ${r.thresholds.small_min_free_ram_mb})`)}
      {row('Origin', <Badge label={r.origin.status} color={r.origin.status === 'approved' ? '#22c55e' : '#dc2626'} title={r.origin.reason || ''} />)}
      {row('Target model', <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.origin.model_name}</span>)}
      {row('Ollama', <Badge label={r.ollama.reachable ? `reachable (${r.ollama.models_loaded ?? 0} models)` : 'unreachable'} color={r.ollama.reachable ? '#22c55e' : '#dc2626'} />)}
      {row('Target present', <Badge label={r.ollama.target_model_present ? 'present' : 'will pull on activate'} color={r.ollama.target_model_present ? '#22c55e' : '#6b7280'} />)}
      {r.served.served_model_name && row('Served tier fits now',
        <Badge label={r.served.current_tier_fits ? 'fits' : 'does NOT fit'} color={r.served.current_tier_fits ? '#22c55e' : '#dc2626'} />)}
      {r.served.downgraded && row('Provisioned downgrade', <Badge label="⬇ downgraded" color="#ea580c" />)}
    </div>
  );
};

export default SLMDeploymentConsole;
